import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Botón "No" que se escapa cuando el usuario intenta tocarlo.
 *
 * Réplica web del `EscapingNoButton` de iOS. Misma mecánica:
 *  - salta a una posición aleatoria cercana al detectar el intento de toque
 *  - respeta los límites del contenedor padre (no sale de la pantalla)
 *  - animación spring (CSS transition con cubic-bezier tipo spring)
 *  - tras varios intentos cambia el texto a algo gracioso
 *
 * Detección: en escritorio usamos `onMouseEnter` (el botón huye cuando el
 * cursor se acerca); en táctil usamos `onTouchStart` (antes de que el tap
 * se complete). Esto cubre los dos modos de interacción de forma fiable.
 */
export interface EscapingNoButtonProps {
  title: string;
  /** Textos que van apareciendo según acumulan intentos. */
  funnyTitles?: string[];
  /** A partir de qué intento se aplica el copy gracioso. */
  funnyTitleThreshold?: number;
  /** Distancia máxima (px) de cada salto. */
  maxJump?: number;
  /** Padding de seguridad respecto a los bordes del contenedor. */
  boundsPadding?: number;
  /** Clases extra para el botón. */
  className?: string;
  /** Acción real (si el usuario logra atraparlo o vía teclado). */
  onClick?: () => void;
  onSelect?: () => void;
}

const DEFAULT_FUNNY: string[] = [
  "Are you sure?",
  "Try again 😏",
  "Not so fast…",
  "Really?",
  "Nope 🙃",
  "Catch me!",
  "Still no?",
  "Persistent! 🏃‍♂️💨"
];

export default function EscapingNoButton({
  title,
  funnyTitles = DEFAULT_FUNNY,
  funnyTitleThreshold = 1,
  maxJump = 190,
  boundsPadding = 6,
  className = ""
}: EscapingNoButtonProps) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [attempts, setAttempts] = useState(0);
  const [hasEscaped, setHasEscaped] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const offsetRef = useRef(offset);
  const lastTargetRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  const escape = useCallback((clientX?: number, clientY?: number) => {
    const button = buttonRef.current;
    const container = containerRef.current;
    if (!button || !container) return;

    const currentOffset = offsetRef.current;
    const buttonRect = button.getBoundingClientRect();
    const origin = {
      left: buttonRect.left - currentOffset.x,
      top: buttonRect.top - currentOffset.y,
      width: buttonRect.width,
      height: buttonRect.height
    };
    const safeRect =
      container.closest("[data-invite-safe-area]")?.getBoundingClientRect() ??
      container.closest(".romantic-invite")?.getBoundingClientRect() ??
      container.parentElement?.getBoundingClientRect() ??
      container.getBoundingClientRect();

    const minX = safeRect.left + boundsPadding - origin.left;
    const maxX = safeRect.right - boundsPadding - origin.width - origin.left;
    const minY = safeRect.top + boundsPadding - origin.top;
    const maxY = safeRect.bottom - boundsPadding - origin.height - origin.top;
    const pointer = clientX != null && clientY != null ? { x: clientX, y: clientY } : null;
    const currentCenter = {
      x: buttonRect.left + buttonRect.width / 2,
      y: buttonRect.top + buttonRect.height / 2
    };
    const candidates = Array.from({ length: 10 }, () => {
      const angle = pointer
        ? Math.atan2(currentCenter.y - pointer.y, currentCenter.x - pointer.x) + rand(-0.9, 0.9)
        : rand(0, Math.PI * 2);
      const distance = rand(maxJump * 0.62, maxJump);
      const jitter = rand(-24, 24);
      return {
        x: clamp(currentOffset.x + Math.cos(angle) * distance + jitter, minX, maxX),
        y: clamp(currentOffset.y + Math.sin(angle) * distance + rand(-18, 18), minY, maxY)
      };
    });

    let next = candidates[0] ?? { x: 0, y: 0 };
    let bestScore = -Infinity;
    for (const candidate of candidates) {
      const candidateCenter = {
        x: origin.left + candidate.x + origin.width / 2,
        y: origin.top + candidate.y + origin.height / 2
      };
      const pointerDistance = pointer
        ? Math.hypot(candidateCenter.x - pointer.x, candidateCenter.y - pointer.y)
        : 0;
      const repeatPenalty = Math.hypot(candidate.x - lastTargetRef.current.x, candidate.y - lastTargetRef.current.y);
      const movement = Math.hypot(candidate.x - currentOffset.x, candidate.y - currentOffset.y);
      const score = pointerDistance + movement * 0.7 + repeatPenalty * 0.35;
      if (score > bestScore) {
        bestScore = score;
        next = candidate;
      }
    }

    lastTargetRef.current = next;
    offsetRef.current = next;
    setOffset(next);
    setAttempts((n) => n + 1);
    setHasEscaped(true);
  }, [maxJump, boundsPadding]);

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      const button = buttonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const proximity = Math.max(92, Math.min(150, rect.width * 0.9));
      if (Math.hypot(event.clientX - centerX, event.clientY - centerY) < proximity) {
        escape(event.clientX, event.clientY);
      }
    };
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    return () => window.removeEventListener("pointermove", onPointerMove);
  }, [escape]);

  const currentTitle = useMemo(() => {
    if (!hasEscaped || attempts < funnyTitleThreshold) return title;
    const idx = (attempts - funnyTitleThreshold) % funnyTitles.length;
    return funnyTitles[idx];
  }, [title, attempts, hasEscaped, funnyTitles, funnyTitleThreshold]);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 flex items-center justify-center"
    >
      <button
        ref={buttonRef}
        type="button"
        aria-label={title}
        className={`btn-secondary invite-choice-button select-none rounded-pill text-base font-semibold ${className}`}
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px)`,
          touchAction: "none",
          // Spring suave: corto, con un poco de overshoot.
          transition:
            "transform 240ms cubic-bezier(0.19, 1, 0.22, 1), opacity 160ms ease"
        }}
        onMouseEnter={(e) => escape(e.clientX, e.clientY)}
        onPointerDownCapture={(e) => {
          e.preventDefault();
          e.stopPropagation();
          escape(e.clientX, e.clientY);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            escape();
          }
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          escape();
        }}
      >
        {currentTitle}
      </button>
    </div>
  );
}

//MARK: - utils

function rand(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max);
}
